import { pick, isEmpty, chain, difference, map, get } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { AdGroupCriterion } from '@bingads-webui/campaign-odata-schemas';
import { modelToViewModelDayTimeTargets, locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import {
  getBundleCountriesAndBundleLocationCriterions,
  shouldSelectBundleOption,
  parseLocationTargets,
  convertLocationStateToLocationSchemaCampaign,
} from '@bingads-webui-campaign/hotel-common-targets';
import { isXandrDisplayAdsCampaign, isAudienceNativeAdsCampaign, isXandrVideoAdsCampaign } from '@bingads-webui-campaign-react/constants';
import { getContextualTargetInfo } from '@bingads-webui-campaign-react/boost-contextual-target';
import { AddedDeviceBidAdjustment as DefaultDeviceBidAdjustment, RemovedDeviceBidAdjustment, AdGroupNegativeReferenceId } from '../common/constants';
import { DeviceKeys } from '../steps/ad-group/target/device';
import { CRITERION_LEVEL, bulkModelToViewModelForLocationTargetsBase } from './criterions';

export const initialAdGroupCriterionsViewModel = {
  data: [],
  isValid: true,
  isDirty: false,
};

const whitelistedSchemaPropertyKeys = Object.keys(AdGroupCriterion.properties);

export const modelToViewModelForAdGroupCriterions = (adGroupCriterionsModel) => {
  const data = pick(adGroupCriterionsModel, whitelistedSchemaPropertyKeys);
  const adGroupCriterionsViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return adGroupCriterionsViewModel;
};

export const modelToViewModelForLocationTargets = ({
  model, campaignLocationModel, bundleCountries, adGroupId, campaignId, overrideOriginalBidAdjustment = false, bidMode = false,
}) => {
  const {
    locations,
    intentId,
    intentOption,
  } = parseLocationTargets(model, 'adGroup', adGroupId);

  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;

  let parentLevelSettings;

  if (campaignLocationModel) {
    const {
      locations: campaignLocations,
      intentId: campaignIntentId,
      intentOption: campaignIntentOption,
    } = parseLocationTargets(campaignLocationModel, 'campaign', campaignId);

    let campaignTargetOption = campaignLocations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
    campaignTargetOption = shouldSelectBundleOption(bundleCountries, campaignLocations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;

    parentLevelSettings = {
      initLocations: campaignLocations,
      currentLocations: campaignLocations,
      targetOption: campaignTargetOption,
      intentId: campaignIntentId,
      ...campaignIntentOption,
    };
  }

  if (overrideOriginalBidAdjustment) {
    locations.forEach((location) => {
      // eslint-disable-next-line no-param-reassign
      location.Criterion.OriginalBidAdjustment = null;
    });
  }

  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      parentLevelSettings,
      bidMode,
      useParentLevel: locations.length === 0,
    },
  };
};

export const modelToViewModelForPlacements = ({
  model,
}) => {
  const placements = model.map(placement => ({
    ...placement.Criterion,
    Id: placement.Id,
  }));

  return {
    placements,
    initialPlacements: placements,
  };
};

export const modelToViewModelForDayTimeTargets = ({
  model,
}) => {
  const adGroupDayTimeRanges = modelToViewModelDayTimeTargets(model);
  return {
    adSchedules: {
      dayTimeRanges: adGroupDayTimeRanges,
    },
  };
};

const modelToViewModelForAudience = audience => ({
  CriterionType: audience.CriterionType,
  Id: audience.Id,
  Status: audience.Status,
  Criterion: {
    AudienceId: audience.AudienceCriterion.AudienceId,
    AudienceName: audience.AudienceCriterion.AudienceName,
    AudienceType: audience.AudienceCriterion.AudienceType,
    BidMultiplier: audience.AudienceCriterion.BidMultiplier,
    Type: audience.AudienceCriterion.Type,
  },
  AdGroup: audience.AdGroup,
  BidType: audience.BidType,
});

export const modelToViewModelForAudienceCriterions = ({ model, separateIMA }) => {
  // TODO: filter custom segment out from audiences
  let filteredAudiences = model.filter(audience => audience.AudienceCriterion.AudienceType !== 'CustomSegment');
  const customSegmentsAudiences = model.filter(audience => audience.AudienceCriterion.AudienceType === 'CustomSegment');
  const interests = separateIMA ? model.filter(audience => audience.AudienceCriterion.AudienceType === 'InMarket') : [];
  if (separateIMA) {
    filteredAudiences = filteredAudiences.filter(audience => audience.AudienceCriterion.AudienceType !== 'InMarket');
  }

  const initialCustomSegments = map(customSegmentsAudiences, modelToViewModelForAudience);

  const initialAudiences = map(filteredAudiences, modelToViewModelForAudience);

  const initialInterests = map(interests, modelToViewModelForAudience);

  return {
    initialAudiences,
    audiences: initialAudiences,
    initialCustomSegments,
    customSegments: initialCustomSegments,
    initialInterests,
    interests: initialInterests,
  };
};

export const bulkModelToViewModelForDayTimeTargets = ({
  criterions,
}) => {
  const schedulingRows = [];

  chain(criterions)
    .filter(criterion => criterion.CriterionType === 'DayTime')
    .each((criterion) => {
      schedulingRows.push({
        Id: criterion.Id,
        Day: criterion.Criterion.DayTimeCriterion.Day,
        StartHour: criterion.Criterion.DayTimeCriterion.FromHour,
        StartMinute: criterion.Criterion.DayTimeCriterion.FromMinute,
        EndHour: criterion.Criterion.DayTimeCriterion.ToHour,
        EndMinute: criterion.Criterion.DayTimeCriterion.ToMinute,
      });
    })
    .value();

  return {
    adSchedules: {
      dayTimeRanges: schedulingRows,
    },
  };
};

export const bulkModelToViewModelForLocationTargets = ({
  locationTargetsModel, bundleCountries,
}) => bulkModelToViewModelForLocationTargetsBase({
  locationTargetsModel, bundleCountries, level: CRITERION_LEVEL.AD_GROUP,
});

export const viewModelToBulkModelForLocationTargets = ({ bundleLocationCriterions, adGroupViewModel, campaignViewModel }) => {
  if (!adGroupViewModel.data.locationTargets || adGroupViewModel.data.locationRestricted) {
    return [];
  }
  const bulkUpsertModel = convertLocationStateToLocationSchemaCampaign(
    adGroupViewModel.data.locationTargets,
    bundleLocationCriterions,
    {
      bidStrategy: { option: 'placeholder' },
      ...campaignViewModel,
    },
    {
      ...adGroupViewModel.data,
      name: {
        value: adGroupViewModel.data.Name,
      },
    }
  );
  return bulkUpsertModel;
};

export const viewModelToBulkModelForAdSchedules = ({ adGroupViewModel, campaignViewModel }) => {
  const targets = adGroupViewModel.data.adSchedulesBulkUpsert || [];
  return targets.map((criterion) => {
    const Status = isEmpty(criterion) ? 'Active' : criterion.Status;
    if (adGroupViewModel.data.Id) {
      return {
        ...criterion,
        Status,
        AdGroup: {
          Id: adGroupViewModel.data.Id,
        },
      };
    }
    return {
      ...criterion,
      Status,
      AdGroupName: adGroupViewModel.data.Name,
      CampaignName: campaignViewModel.Name,
    };
  });
};

export const viewModelToBulkModelForAudiences = ({ adGroupViewModel, campaignViewModel }) => {
  if (adGroupViewModel.data.audienceRestricted) {
    return [];
  }
  const audiences = adGroupViewModel.data.audiences || [];
  const initialAudiences = adGroupViewModel.data.initialAudiences || [];
  const customSegments = adGroupViewModel.data.customSegments || [];
  const initialCustomSegments = adGroupViewModel.data.initialCustomSegments || [];
  const interests = adGroupViewModel.data.interests || [];
  const initialInterests = adGroupViewModel.data.initialInterests || [];

  const totalAudiences = [...audiences, ...interests];
  const initialTotalAudiences = [...initialAudiences, ...initialInterests];

  const added = totalAudiences.filter(audience => !initialTotalAudiences.find(initialAudience => initialAudience.Criterion.AudienceId === audience.Criterion.AudienceId && initialAudience.Criterion.BidMultiplier === audience.Criterion.BidMultiplier));
  const removed = initialTotalAudiences.filter(initialAudience => !totalAudiences.find(audience => audience.Criterion.AudienceId === initialAudience.Criterion.AudienceId)).map(audience => ({
    ...audience,
    Status: 'Deleted',
  }));

  const addedCustomSegments = customSegments.filter(customSegment => !initialCustomSegments.find(initialCustomSegment => initialCustomSegment.Criterion.AudienceId === customSegment.Criterion.AudienceId));
  const removedCustomSegments = initialCustomSegments.filter(initialCustomSegment => !customSegments.find(customSegment => customSegment.Criterion.AudienceId === initialCustomSegment.Criterion.AudienceId)).map(customSegment => ({
    ...customSegment,
    Status: 'Deleted',
  }));

  return [...added, ...addedCustomSegments, ...removed, ...removedCustomSegments].map((audience) => {
    const Status = isEmpty(audience.Status) ? 'Active' : audience.Status;

    const typePrefix = audience.Status === 'Excluded' ? 'Negative' : '';
    const criterionType = `${typePrefix}${audience.Criterion.AudienceType}`;

    if (adGroupViewModel.data.Id) {
      return {
        ...audience,
        Status,
        CriterionType: criterionType,
        AdGroup: {
          Id: adGroupViewModel.data.Id,
        },
      };
    }

    return {
      ...audience,
      Status,
      CriterionType: criterionType,
      AdGroupName: adGroupViewModel.data.Name,
      CampaignName: campaignViewModel.Name,
      AdGroup: {
        Id: AdGroupNegativeReferenceId,
      },
    };
  });
};

export const viewModelToBulkModelForPlacements = ({ adGroupViewModel, campaignViewModel }) => {
  const placements = adGroupViewModel.data.placements || [];
  const initialPlacements = adGroupViewModel.data.initialPlacements || [];

  const added = placements.filter(placement => !initialPlacements.find(initialPlacement => initialPlacement.PlacementId === placement.PlacementId));
  const removed = initialPlacements.filter(initialPlacement => !placements.find(placement => placement.PlacementId === initialPlacement.PlacementId)).map(placement => ({
    ...placement,
    Status: 'Deleted',
  }));

  return [...added, ...removed].map((placement) => {
    const Status = isEmpty(placement.Status) ? 'Active' : placement.Status;

    const adGroupId = adGroupViewModel.data.Id;
    if (adGroupId) {
      return {
        Status,
        CriterionType: 'AdGroupPlacementCriterion',
        ...(placement.Id ? { Id: placement.Id } : {}),
        Criterion: {
          PlacementId: placement.PlacementId,
          Type: 'Placement',
        },
        AdGroup: {
          Id: adGroupId,
        },
      };
    }

    return {
      Status,
      ...(placement.Id ? { Id: placement.Id } : {}),
      CriterionType: 'AdGroupPlacementCriterion',
      AdGroupName: adGroupViewModel.data.Name,
      CampaignName: campaignViewModel.Name,
      Criterion: {
        PlacementId: placement.PlacementId,
        Type: 'Placement',
      },
      AdGroup: {
        Id: AdGroupNegativeReferenceId,
      },
    };
  });
};

export const getTargetCriterion = ({
  targets,
  type,
  propKey,
  adGroupViewModel,
  campaignViewModel,
  bidAdjustment,
  overrideType = undefined,
  level = 'adGroup',
}) => targets.map((target) => {
  let BidAdjustment = bidAdjustment;
  let propValue = target.criterion;

  if (typeof (target.criterion) === 'object') {
    propValue = target.criterion.Target;

    if (bidAdjustment === undefined) {
      const { BidAdjustment: bid } = target.criterion;
      BidAdjustment = bid;
    }
  }

  const criterion = {
    Type: overrideType || type,
    BidAdjustment,
    [`${overrideType || type}Criterion`]: {
      [propKey]: propValue,
    },
  };

  if (level === 'campaign') {
    return {
      Id: target.Id,
      Status: target.status ? target.status : 'Active',
      Criterion: criterion,
      CriterionType: type,
      Campaign: {
        Id: campaignViewModel.data.Id || -111,
      },
    };
  }

  if (adGroupViewModel.data.Id) {
    return {
      Id: target.Id,
      Status: target.status ? target.status : 'Active',
      Criterion: criterion,
      CriterionType: type,
      AdGroup: {
        Id: adGroupViewModel.data.Id,
      },
      CampaignName: campaignViewModel.Name,
    };
  }
  return {
    Criterion: criterion,
    CriterionType: type,
    AdGroupName: adGroupViewModel.data.Name,
    CampaignName: campaignViewModel.Name,
    AdGroup: {
      Id: AdGroupNegativeReferenceId,
    },
  };
});

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function getDeviceCriterionsFull(adGroupViewModel, isDeviceTargetingForDisplayEnabled) {
  const deviceTargets = adGroupViewModel.data.deviceTypes || [];
  const initialDeviceTargets = adGroupViewModel.data.initialDeviceTypes || [];

  const addedBackDevices = initialDeviceTargets.filter(initialTarget => deviceTargets.find(target => initialTarget.criterion === target) && initialTarget.bidAdjustment === RemovedDeviceBidAdjustment)
    .map((device => ({ criterion: device.criterion, Id: device.Id })));
  const newlyAddedDevices = deviceTargets.filter(target => initialDeviceTargets.find(initialTarget => initialTarget.criterion === target) === undefined)
    .filter((device => addedBackDevices.find(addedBackDevice => addedBackDevice.criterion === device) === undefined));
  let removedDevices = initialDeviceTargets.filter(initialTarget => deviceTargets.find(target => initialTarget.criterion === target) === undefined);
  if (isDeviceTargetingForDisplayEnabled) {
    removedDevices = initialDeviceTargets.filter(initialTarget => deviceTargets.find(target => initialTarget.criterion === target) === undefined && initialTarget.bidAdjustment === DefaultDeviceBidAdjustment);
  }
  const addedDevices = [...addedBackDevices, ...newlyAddedDevices.map(criterion => ({ criterion }))];

  return { addedDevices, removedDevices };
}

function getDeviceCriterionsVideoAds(adGroupViewModel) {
  const deviceTargets = adGroupViewModel.data.deviceTypes || []; // user selected device targets

  // ICM 542906055, handles a case where no device criterion was previously saved
  const deviceTargetsWhenNoInitialDeviceTargets = [
    {
      criterion: DeviceKeys.Computers,
      bidAdjustment: deviceTargets.includes(DeviceKeys.Computers) ? DefaultDeviceBidAdjustment : RemovedDeviceBidAdjustment,
    },
    {
      criterion: DeviceKeys.Tablets,
      bidAdjustment: deviceTargets.includes(DeviceKeys.Tablets) ? DefaultDeviceBidAdjustment : RemovedDeviceBidAdjustment,
    },
    {
      criterion: DeviceKeys.Smartphones,
      bidAdjustment: deviceTargets.includes(DeviceKeys.Smartphones) ? DefaultDeviceBidAdjustment : RemovedDeviceBidAdjustment,
    },
    {
      criterion: DeviceKeys.ConnectedTVs,
      bidAdjustment: RemovedDeviceBidAdjustment,
    },
  ];

  const initialDeviceTargets = adGroupViewModel.data.initialDeviceTypes || []; // saved initial device targets
  const supportedDevices = [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones]; // supported device targets for VideoAds

  // get added devices - overlap between initial removed devices (bid adjustment = -100) and selected devices
  const addedDevices = initialDeviceTargets.length === 0 ? deviceTargetsWhenNoInitialDeviceTargets : initialDeviceTargets.filter(initialTarget => deviceTargets.find(target => initialTarget.criterion === target) && initialTarget.bidAdjustment === RemovedDeviceBidAdjustment);

  // get removed devices - overlap between supported added devices (bid adjustment = 0) and selected devices
  const toRemoveDevices = difference(supportedDevices, deviceTargets);
  const removedDevices = initialDeviceTargets.filter(initialTarget => toRemoveDevices.find(target => initialTarget.criterion === target) && initialTarget.bidAdjustment === DefaultDeviceBidAdjustment);

  return { addedDevices, removedDevices };
}

function getDeviceCriterions(adGroupViewModel) {
  const { deviceTypesWithBidAdjustment = [] } = adGroupViewModel.data;
  const deviceTargets = adGroupViewModel.data.deviceTypes || [];
  const initialDeviceTargets = adGroupViewModel.data.initialDeviceTypes || [];
  const supportedDevices = [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones];

  // if we have deviceTargetingCampaignLevel, we need to delete the targets set at adgroup level.
  const deviceTargetingCampaignLevel = adGroupViewModel.data.deviceTargetingCampaignLevel || false;

  if (deviceTargetingCampaignLevel) {
    return { addedDevices: [], removedDevices: initialDeviceTargets.map(target => ({ ...target, status: 'Deleted' })) };
  }

  const isBidAdjustmentSame = initialTarget => deviceTypesWithBidAdjustment.find(targetWithBid =>
    targetWithBid.Criterion.DeviceCriterion.DeviceName === initialTarget.target && targetWithBid.Criterion.BidAdjustment === initialTarget.bidAdjustment);
  const isTargetChanged = target => initialDeviceTargets.find(initialTarget => initialTarget.criterion === target && isBidAdjustmentSame(initialTarget)) === undefined;

  const addedDevices = deviceTargets.filter(isTargetChanged).map((target) => {
    const deviceTargetWithBid = deviceTypesWithBidAdjustment.find(deviceWithBid => deviceWithBid.Criterion.DeviceCriterion.DeviceName === target) || {};
    const initialDeviceTarget = initialDeviceTargets.find(initialTarget => initialTarget.criterion === target) || {};
    const { Id } = initialDeviceTarget || deviceTargetWithBid;
    const bid = get(deviceTargetWithBid, 'Criterion.BidAdjustment', initialDeviceTarget.bidAdjustment || DefaultDeviceBidAdjustment);

    return {
      Id,
      criterion: {
        Target: target,
        BidAdjustment: bid,
      },
    };
  });

  const toRemoveDevices = initialDeviceTargets.length === 0 && deviceTargets.length === 0 ? [] : difference(supportedDevices, deviceTargets); // don't remove devices if no change

  const removedDevices = toRemoveDevices.map((target) => {
    const { Id } = initialDeviceTargets.find(initialTarget => initialTarget.criterion === target) || {};

    return {
      Id,
      criterion: {
        Target: target,
        BidAdjustment: RemovedDeviceBidAdjustment,
      },
    };
  });

  return { addedDevices, removedDevices };
}


const isBidAdjustmentSame = (criterionType, targetWithBidAdjustmentList, target, initialTarget) =>
  targetWithBidAdjustmentList.find(targetWithBid =>
    targetWithBid.Criterion[`${criterionType}Criterion`].Target === target &&
    targetWithBid.Criterion.BidAdjustment === initialTarget.bidAdjustment);

const hydrateCriterionWithBidAndId = (criterionType, initialTargetList, adddedCriterionList, targetWithBidAdjustmentList) => {
  for (let i = 0; i < adddedCriterionList.length; i += 1) {
    const criterion = adddedCriterionList[i];
    // add bid adjustments to modified criterion
    const matchingAgeRangeWithBid = targetWithBidAdjustmentList.find(criterionWithBidAdjustment =>
      criterionWithBidAdjustment.Criterion[`${criterionType}Criterion`].Target === criterion.Criterion[`${criterionType}Criterion`].Target);
    criterion.Criterion.BidAdjustment = matchingAgeRangeWithBid.Criterion.BidAdjustment;

    // add id to update existing criterion in MT
    const originalCrition = initialTargetList.find(initialTarget => initialTarget.criterion === criterion.Criterion[`${criterionType}Criterion`].Target);
    if (originalCrition) {
      criterion.Id = originalCrition.Id;
    }
  }
};

export const viewModelToBulkModelForTargeting = ({
  adGroupViewModel,
  campaignViewModel,
  isXandrDeviceTargetEnabled,
  isBoostEnabled = false,
  isDeviceTargetingForDisplayEnabled = false,
  isMultiAdFormatEnabled = false,
  isXandrServing = false,
  isPremiumStreamingEnabled = false,
}) => {
  let addedDeviceCriterion = [];
  let removedDeviceCriterion = [];
  // deviceTypes
  const isDisplayAds = isXandrDisplayAdsCampaign(campaignViewModel.CampaignSubType);
  const isNativeAds = isAudienceNativeAdsCampaign(campaignViewModel.CampaignSubType);
  const isVideoAds = isXandrVideoAdsCampaign(campaignViewModel.CampaignSubType);
  const isDealBasedAds = adGroupViewModel.data.deal;

  const isXandrDeviceTargetForOLVEnabled = isXandrDeviceTargetEnabled && isVideoAds && campaignViewModel.BiddingScheme.Type === 'ManualCpm';
  const isUpdate = !!adGroupViewModel.data.Id;

  let addedDevices = [];
  let removedDevices = [];

  if (isUpdate) {
    // updating
    if (isDisplayAds) {
      const { addedDevices: displayAddDevices, removedDevices: displayRemovedDevices } = getDeviceCriterionsFull(adGroupViewModel, isDeviceTargetingForDisplayEnabled);

      addedDevices = displayAddDevices;
      removedDevices = displayRemovedDevices;
    } else if (isNativeAds) {
      const { addedDevices: displayAddDevices, removedDevices: displayRemovedDevices } = getDeviceCriterions(adGroupViewModel);

      addedDevices = displayAddDevices;
      removedDevices = displayRemovedDevices;
    } else if (isDealBasedAds) {
      if (!adGroupViewModel.data.deviceRestricted) {
        addedDevices = adGroupViewModel.data.deviceTypes.map(device => ({ criterion: device }));
        removedDevices = difference(Object.keys(DeviceKeys), adGroupViewModel.data.deviceTypes).map(device => ({ criterion: device }));
      }
    } else if (isVideoAds && (!isXandrServing || isXandrDeviceTargetForOLVEnabled)) {
      const { addedDevices: displayAddDevices, removedDevices: displayRemovedDevices } = getDeviceCriterionsVideoAds(adGroupViewModel);

      addedDevices = displayAddDevices;
      removedDevices = displayRemovedDevices;
    } else if (isXandrDeviceTargetForOLVEnabled) {
      addedDevices = adGroupViewModel.data.deviceTypes.map(device => ({ criterion: device }));
      removedDevices = difference(Object.keys(DeviceKeys), adGroupViewModel.data.deviceTypes).map(device => ({ criterion: device }));
    } else if (isVideoAds && isPremiumStreamingEnabled && campaignViewModel.BiddingScheme.Type === 'ManualCpv') {
      addedDevices = adGroupViewModel.data.deviceTypes.map(device => ({ criterion: device }));
      removedDevices = difference(Object.keys(DeviceKeys), adGroupViewModel.data.deviceTypes).map(device => ({ criterion: device }));
    } else {
      const cpmTargets = [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones];
      const cpvTargets = [DeviceKeys.ConnectedTVs];
      if (campaignViewModel.BiddingScheme.Type === 'ManualCpm') {
        addedDevices = cpmTargets.map(device => ({ criterion: device }));
        removedDevices = cpvTargets.map(device => ({ criterion: device }));
      } else {
        addedDevices = cpvTargets.map(device => ({ criterion: device }));
        removedDevices = cpmTargets.map(device => ({ criterion: device }));
      }
    }
  } else {
    // creating
    // eslint-disable-next-line no-lonely-if
    if (isDisplayAds || (isNativeAds && isMultiAdFormatEnabled)) {
      const supportedDevices = [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones];
      addedDevices = adGroupViewModel.data.deviceTypes.map(device => ({ criterion: device }));
      removedDevices = difference(supportedDevices, adGroupViewModel.data.deviceTypes).map(device => ({ criterion: device }));
    } else if (isDealBasedAds) {
      if (!adGroupViewModel.data.deviceRestricted) {
        addedDevices = adGroupViewModel.data.deviceTypes.map(device => ({ criterion: device }));
        removedDevices = difference(Object.keys(DeviceKeys), adGroupViewModel.data.deviceTypes).map(device => ({ criterion: device }));
      }
    } else if (isXandrDeviceTargetForOLVEnabled) {
      addedDevices = adGroupViewModel.data.deviceTypes.map(device => ({ criterion: device }));
      removedDevices = difference(Object.keys(DeviceKeys), adGroupViewModel.data.deviceTypes).map(device => ({ criterion: device }));
    } else if (isVideoAds && isPremiumStreamingEnabled && campaignViewModel.BiddingScheme.Type === 'ManualCpv') {
      addedDevices = adGroupViewModel.data.deviceTypes.map(device => ({ criterion: device }));
      removedDevices = difference(Object.keys(DeviceKeys), adGroupViewModel.data.deviceTypes).map(device => ({ criterion: device }));
    }
  }

  // deviceTypesWithBidAdjustment is not set then use DefaultDeviceBidAdjustment for all added devices
  const { deviceTypesWithBidAdjustment } = adGroupViewModel.data;
  const defaultBidAdjustment = deviceTypesWithBidAdjustment ? undefined : DefaultDeviceBidAdjustment;

  addedDeviceCriterion = getTargetCriterion({
    targets: addedDevices, type: 'Device', propKey: 'DeviceName', adGroupViewModel, campaignViewModel, bidAdjustment: defaultBidAdjustment,
  });

  // ICM 542906055, in this scenario, the getTargetCriterion function sets the bid adjustment to 0 for all devices
  if (isVideoAds && !isXandrServing && adGroupViewModel.data.initialDeviceTypes.length === 0) {
    const deviceTypes = [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones, DeviceKeys.ConnectedTVs];
    const adjustedAddedDeviceCriterion = addedDeviceCriterion.map((addedCriterion, idx) => {
      if (deviceTypes.includes(addedCriterion.Criterion.DeviceCriterion.DeviceName)) {
        return {
          ...addedCriterion,
          Criterion: {
            ...addedCriterion.Criterion,
            BidAdjustment: addedDevices[idx].bidAdjustment,
          },
        };
      }
      return addedCriterion;
    });
    addedDeviceCriterion = adjustedAddedDeviceCriterion;
  }

  removedDeviceCriterion = getTargetCriterion({
    targets: removedDevices, type: 'Device', propKey: 'DeviceName', adGroupViewModel, campaignViewModel, bidAdjustment: RemovedDeviceBidAdjustment,
  });

  // ageRanges
  const ageTargets = adGroupViewModel.data.ageRanges || [];
  const initialAgeTargets = adGroupViewModel.data.initialAgeRanges || [];
  const { ageRangesWithBidAdjustment } = adGroupViewModel.data;
  const addedAges = ageTargets.filter(target => initialAgeTargets.find(initialTarget => initialTarget.criterion === target
    && (!ageRangesWithBidAdjustment || isBidAdjustmentSame('Age', ageRangesWithBidAdjustment, target, initialTarget))) === undefined);
  const removedAges = initialAgeTargets.filter(initialTarget => ageTargets.find(target => initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
  const addedAgeCriterion = getTargetCriterion({
    targets: addedAges.map(criterion => ({ criterion })), type: 'Age', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });
  const removedAgeCriterion = getTargetCriterion({
    targets: removedAges, type: 'Age', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });

  const negativeAgeRanges = adGroupViewModel.data.negativeAgeRanges || [];
  const removedNegativeAges = negativeAgeRanges.filter(negativeAge => addedAges.find(addedTarget => addedTarget === negativeAge.criterion)).map(target => ({ ...target, status: 'Deleted' }));
  const removedNegativeAgeCriterion = getTargetCriterion({
    targets: removedNegativeAges, type: 'NegativeAge', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });

  if (ageRangesWithBidAdjustment) {
    hydrateCriterionWithBidAndId('Age', initialAgeTargets, addedAgeCriterion, ageRangesWithBidAdjustment);
  }
  // genderTypes
  const genderTypes = adGroupViewModel.data.genderTypes || [];
  const initialGenderTypes = adGroupViewModel.data.initialGenderTypes || [];
  const { genderTypesWithBidAdjustment } = adGroupViewModel.data;
  const addedGenders = genderTypes.filter(target => initialGenderTypes.find(initialTarget => initialTarget.criterion === target
    && (!genderTypesWithBidAdjustment || isBidAdjustmentSame('Gender', genderTypesWithBidAdjustment, target, initialTarget))) === undefined);
  const removedGenders = initialGenderTypes.filter(initialTarget => genderTypes.find(target => initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
  const addedGenderCriterion = getTargetCriterion({
    targets: addedGenders.map(criterion => ({ criterion })), type: 'Gender', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });
  const removedGenderCriterion = getTargetCriterion({
    targets: removedGenders, type: 'Gender', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });

  const negativeGenderTypes = adGroupViewModel.data.negativeGenderTYpes || [];
  const removedNegativeGenders = negativeGenderTypes.filter(negativeGenderType => addedGenders.find(addedTarget => addedTarget === negativeGenderType.criterion)).map(target => ({ ...target, status: 'Deleted' }));
  const removedNegativeGenderCriterion = getTargetCriterion({
    targets: removedNegativeGenders, type: 'NegativeGender', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });

  if (genderTypesWithBidAdjustment) {
    hydrateCriterionWithBidAndId('Gender', initialGenderTypes, addedGenderCriterion, genderTypesWithBidAdjustment);
  }

  // genreTargets
  const genreTargets = adGroupViewModel.data.genreTargets || [];
  const initialGenreTargets = adGroupViewModel.data.initialGenreTargets || [];
  const addedGenres = genreTargets.filter(target => initialGenreTargets.find(initialTarget => initialTarget.criterion === target) === undefined);
  const removedGenres = initialGenreTargets.filter(initialTarget => genreTargets.find(target => initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
  const addedGenreTargets = getTargetCriterion({
    targets: addedGenres.map(criterion => ({ criterion })), type: 'Genre', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });
  const removedGenreCriterion = getTargetCriterion({
    targets: removedGenres, type: 'Genre', propKey: 'Target', adGroupViewModel, campaignViewModel,
  });

  // industry target
  const { data: { industryTargetsBidAdjustment } } = adGroupViewModel;
  let industryTargets = [];
  let negativeIndustryTargets = [];
  let initialIndustryTargets = [];
  let initialNegativeIndustryTargets = [];
  let addedIndustryTargets = [];
  let removedIndustryTargets = [];
  let addedIndustryCriterion = [];
  let removedIndustryCriterion = [];
  let negativeAddedIndustryTargets = [];
  let negativeRemovedIndustryTargets = [];
  let negativeAddedIndustryCriterion = [];
  let negativeRemovedIndustryCriterion = [];

  if ((!isXandrServing && isDisplayAds) || (!isXandrServing && isVideoAds) || isNativeAds) {
    industryTargets = adGroupViewModel.data.industry || [];
    initialIndustryTargets = adGroupViewModel.data.initialIndustryTargets || [];

    negativeIndustryTargets = adGroupViewModel.data.negativeIndustry || [];
    initialNegativeIndustryTargets = adGroupViewModel.data.initialNegativeIndustryTargets || [];

    const isIndustryTargetBidAdjustmentUpdated = ({ criterion, BidAdjustment }) => industryTargetsBidAdjustment.find(targetWithBid =>
      get(targetWithBid, 'Criterion.IndustryCriterion.Target') === criterion && get(targetWithBid, 'Criterion.BidAdjustment') === BidAdjustment);

    addedIndustryTargets = industryTargets.filter(target => initialIndustryTargets.find(initialTarget =>
      initialTarget.criterion === target && (!industryTargetsBidAdjustment || isIndustryTargetBidAdjustmentUpdated(initialTarget))) === undefined);
    removedIndustryTargets = initialIndustryTargets.filter(initialTarget => industryTargets.find(target => initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
    addedIndustryCriterion = getTargetCriterion({
      targets: addedIndustryTargets.map(criterion => ({ criterion })), type: 'Industry', propKey: 'Target', adGroupViewModel, campaignViewModel,
    });

    removedIndustryCriterion = getTargetCriterion({
      targets: removedIndustryTargets, type: 'Industry', propKey: 'Target', adGroupViewModel, campaignViewModel,
    });

    negativeAddedIndustryTargets = negativeIndustryTargets.filter(target => initialNegativeIndustryTargets.find(initialTarget => initialTarget.criterion === target) === undefined);
    negativeRemovedIndustryTargets = initialNegativeIndustryTargets.filter(initialTarget => negativeIndustryTargets.find(target => initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
    negativeAddedIndustryCriterion = getTargetCriterion({
      targets: negativeAddedIndustryTargets.map(criterion => ({ criterion })), type: 'NegativeIndustry', propKey: 'Target', adGroupViewModel, campaignViewModel, overrideType: 'Industry',
    });

    negativeRemovedIndustryCriterion = getTargetCriterion({
      targets: negativeRemovedIndustryTargets, type: 'NegativeIndustry', propKey: 'Target', adGroupViewModel, campaignViewModel, overrideType: 'Industry',
    });

    if (industryTargetsBidAdjustment && industryTargetsBidAdjustment.length > 0) {
      hydrateCriterionWithBidAndId('Industry', initialIndustryTargets, addedIndustryCriterion, industryTargetsBidAdjustment);
    }
  }

  // job target
  const { data: { jobTargetBidAdjustments } } = adGroupViewModel;
  let jobTargets = [];
  let negativeJobTargets = [];
  let initialJobTargets = [];
  let initialNegativeJobTargets = [];
  let addedJobTargets = [];
  let removedJobTargets = [];
  let addedJobCriterion = [];
  let removedJobCriterion = [];
  let negativeAddedJobTargets = [];
  let negativeRemovedJobTargets = [];
  let negativeAddedJobCriterion = [];
  let negativeRemovedJobCriterion = [];

  if ((!isXandrServing && isDisplayAds) || (!isXandrServing && isVideoAds) || isNativeAds) {
    jobTargets = adGroupViewModel.data.jobTargets || [];
    initialJobTargets = adGroupViewModel.data.initialJobTargets || [];

    negativeJobTargets = adGroupViewModel.data.negativeJobTargets || [];
    initialNegativeJobTargets = adGroupViewModel.data.initialNegativeJobTargets || [];

    const isJobTargetBidAdjustmentUpdated = ({ criterion, BidAdjustment }) => jobTargetBidAdjustments.find(targetWithBid =>
      get(targetWithBid, 'Criterion.JobFunctionCriterion.Target') === criterion && get(targetWithBid, 'Criterion.BidAdjustment') === BidAdjustment);

    addedJobTargets = jobTargets.filter(target => initialJobTargets.find(initialTarget =>
      initialTarget.criterion === target && (!jobTargetBidAdjustments || isJobTargetBidAdjustmentUpdated(initialTarget))) === undefined);
    removedJobTargets = initialJobTargets.filter(initialTarget => jobTargets.find(target => initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
    addedJobCriterion = getTargetCriterion({
      targets: addedJobTargets.map(criterion => ({ criterion })), type: 'JobFunction', propKey: 'Target', adGroupViewModel, campaignViewModel,
    });

    removedJobCriterion = getTargetCriterion({
      targets: removedJobTargets, type: 'JobFunction', propKey: 'Target', adGroupViewModel, campaignViewModel,
    });

    negativeAddedJobTargets = negativeJobTargets.filter(target => initialNegativeJobTargets.find(initialTarget => initialTarget.criterion === target) === undefined);
    negativeRemovedJobTargets = initialNegativeJobTargets.filter(initialTarget => negativeJobTargets.find(target => initialTarget.criterion === target) === undefined).map(target => ({ ...target, status: 'Deleted' }));
    negativeAddedJobCriterion = getTargetCriterion({
      targets: negativeAddedJobTargets.map(criterion => ({ criterion })), type: 'NegativeJobFunction', propKey: 'Target', adGroupViewModel, campaignViewModel, overrideType: 'JobFunction',
    });

    negativeRemovedJobCriterion = getTargetCriterion({
      targets: negativeRemovedJobTargets, type: 'NegativeJobFunction', propKey: 'Target', adGroupViewModel, campaignViewModel, overrideType: 'JobFunction',
    });

    if (jobTargetBidAdjustments && jobTargetBidAdjustments.length > 0) {
      hydrateCriterionWithBidAndId('JobFunction', initialJobTargets, addedJobCriterion, jobTargetBidAdjustments);
    }
  }
  // companyName targets
  let companyNameTargets = [];
  let negativeCompanyNameTargets = [];
  let initialCompanyNameTargets = [];
  let initialNegativeCompanyNameTargets = [];
  let addedCompanyNameTargets = [];
  let removedCompanyNameTargets = [];
  let addedCompanyNameCriterion = [];
  let removedCompanyNameCriterion = [];
  let negativeAddedCompanyNameTargets = [];
  let negativeRemovedCompanyNameTargets = [];
  let negativeAddedCompanyNameCriterion = [];
  let negativeRemovedCompanyNameCriterion = [];

  if ((!isXandrServing && isDisplayAds) || (!isXandrServing && isVideoAds) || isNativeAds) {
    companyNameTargets = adGroupViewModel.data.companyNameTargets || [];
    initialCompanyNameTargets = adGroupViewModel.data.initialCompanyNameTargets || [];


    negativeCompanyNameTargets = adGroupViewModel.data.negativeCompanyNameTargets || [];
    initialNegativeCompanyNameTargets = adGroupViewModel.data.initialNegativeCompanyNameTargets || [];

    addedCompanyNameTargets = companyNameTargets.filter(target => initialCompanyNameTargets.find(initialTarget =>
      initialTarget.Target === target.Target && initialTarget.BidAdjustment === target.BidAdjustment) === undefined);
    removedCompanyNameTargets = initialCompanyNameTargets.filter(initialTarget => companyNameTargets.find(target => initialTarget.Target === target.Target) === undefined).map(target => ({ ...target, criterion: target.Target, status: 'Deleted' }));

    addedCompanyNameCriterion = getTargetCriterion({
      targets: addedCompanyNameTargets.map(({ Id, ...criterion }) => ({ Id, criterion })), type: 'CompanyName', propKey: 'Target', adGroupViewModel, campaignViewModel,
    });

    removedCompanyNameCriterion = getTargetCriterion({
      targets: removedCompanyNameTargets, type: 'CompanyName', propKey: 'Target', adGroupViewModel, campaignViewModel,
    });

    negativeAddedCompanyNameTargets = negativeCompanyNameTargets.filter(target => initialNegativeCompanyNameTargets.find(initialTarget => initialTarget.Target === target.Target) === undefined);
    negativeRemovedCompanyNameTargets = initialNegativeCompanyNameTargets.filter(initialTarget => negativeCompanyNameTargets.find(target => initialTarget.Target === target.Target) === undefined).map(target => ({ ...target, criterion: target.Target, status: 'Deleted' }));

    negativeAddedCompanyNameCriterion = getTargetCriterion({
      targets: negativeAddedCompanyNameTargets.map(criterion => ({ criterion })), type: 'NegativeCompanyName', propKey: 'Target', adGroupViewModel, campaignViewModel, overrideType: 'CompanyName',
    });

    negativeRemovedCompanyNameCriterion = getTargetCriterion({
      targets: negativeRemovedCompanyNameTargets, type: 'NegativeCompanyName', propKey: 'Target', adGroupViewModel, campaignViewModel, overrideType: 'CompanyName',
    });
  }

  let initialContextualTargets = [];
  let contextualTargets = [];
  let updatedContextualTargets = [];

  if (isAudienceNativeAdsCampaign(campaignViewModel.CampaignSubType) && isBoostEnabled) {
    initialContextualTargets = adGroupViewModel.data.initialContextualTargets || [];
    contextualTargets = adGroupViewModel.data.contextualTargets || [];

    updatedContextualTargets = getContextualTargetInfo(initialContextualTargets, contextualTargets).map((criterion) => {
      // eslint-disable-next-line no-param-reassign
      criterion.AdGroup = { Id: adGroupViewModel.data.Id ? adGroupViewModel.data.Id : AdGroupNegativeReferenceId };
      return criterion;
    }) || [];
  }

  return [
    ...addedDeviceCriterion,
    ...removedDeviceCriterion,
    ...removedNegativeAgeCriterion,
    ...addedAgeCriterion,
    ...removedAgeCriterion,
    ...removedNegativeGenderCriterion,
    ...addedGenderCriterion,
    ...removedGenderCriterion,
    ...addedGenreTargets,
    ...removedGenreCriterion,
    ...addedIndustryCriterion,
    ...removedIndustryCriterion,
    ...negativeAddedIndustryCriterion,
    ...negativeRemovedIndustryCriterion,
    ...addedJobCriterion,
    ...removedJobCriterion,
    ...negativeAddedJobCriterion,
    ...negativeRemovedJobCriterion,
    ...addedCompanyNameCriterion,
    ...removedCompanyNameCriterion,
    ...negativeAddedCompanyNameCriterion,
    ...negativeRemovedCompanyNameCriterion,
    ...updatedContextualTargets,
  ];
};

export const loadBundleCountries = ({
  cid,
  aid,
  serverContext,
}) => {
  const lcid = serverContext ? serverContext.CurrentUser.LocaleID : '1033';
  const market = serverContext ? serverContext.CurrentUser.Market : 'US';
  const language = 'All';

  return getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, language, market);
};

const adGroupCriterionTypeName = 'Model.AdGroupCriterion';

export const readAdGroupCriterions = ({
  cid,
  aid,
  campaignId,
  adGroupId,
  type,
}) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdGroups.$withKey(adGroupId)
    .AdGroupCriterions
    .$cast(adGroupCriterionTypeName)
    .get({
      filter: {
        where: type,
      },
    })
    .toPromise();

export const readPlacementCriterions = ({
  cid,
  aid,
  campaignId,
  adGroupId,
}) =>
  readAdGroupCriterions({
    cid,
    aid,
    campaignId,
    adGroupId,
    type: "isof(Criterion, 'Model.PlacementCriterion')",
  });

export const readLocationCriterions = ({
  cid,
  aid,
  campaignId,
  adGroupId,
}) =>
  readAdGroupCriterions({
    cid,
    aid,
    campaignId,
    adGroupId,
    type: "isof(Criterion,'Model.LocationCriterion') or isof(Criterion,'Model.RadiusCriterion') or isof(Criterion,'Model.LocationIntentCriterion')",
  });

export const readDayTimeCriterions = ({
  cid,
  aid,
  campaignId,
  adGroupId,
}) =>
  readAdGroupCriterions({
    cid,
    aid,
    campaignId,
    adGroupId,
    type: "isof(Criterion,'Model.DayTimeCriterion')",
  });

export const readTargetingCriterions = ({
  cid,
  aid,
  campaignId,
  adGroupId,
}) =>
  readAdGroupCriterions({
    cid,
    aid,
    campaignId,
    adGroupId,
    type: "isof(Criterion,'Model.AgeCriterion') or isof(Criterion,'Model.GenderCriterion') or isof(Criterion,'Model.DeviceCriterion') or isof(Criterion,'Model.GenreCriterion') or isof(Criterion, 'Model.IndustryCriterion') or isof(Criterion, 'Model.JobFunctionCriterion') or isof(Criterion, 'Model.CompanyNameCriterion') or isof(Criterion, 'Model.ContextualTarget')",
  });

export const readAudienceCriterions = ({
  cid,
  aid,
  campaignId,
  adGroupId,
}) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AdGroups.$withKey(adGroupId)
    .AudienceAssociations
    .get({ top: 2000 })
    .toPromise();
